import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

/**
 * Interface for Group data structure
 */
export interface Group {
  id: string;
  name: string;
  description: string;
  // Add other properties as needed
}

@Component({
  selector: 'lib-groups',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './groups.component.html',
  styleUrl: './groups.component.css'
})
export class GroupsComponent {
  /**
   * List of groups to display
   */
  @Input({ required: true }) groups: any[] = [];
  
  /**
   * Organization code
   */
  @Input() orgCode: string = '';
  
  /**
   * Event emitted when "+ Add Users" is clicked for a group
   */
  @Output() addUsers = new EventEmitter<Group>();
  
  /**
   * Event emitted when a new group is saved
   */
  @Output() createGroup = new EventEmitter<{ name: string, description: string }>();
  
  /**
   * Event emitted when group creation is cancelled
   */
  @Output() cancelCreateGroup = new EventEmitter<void>();

  /**
   * Event emitted when delete button is clicked for a group
   */
  @Output() deleteGroup = new EventEmitter<Group>();

  /**
   * Event emitted when "View Members" is clicked for a group
   */
  @Output() viewMembers = new EventEmitter<Group>();

  /**
   * Flag to control visibility of the create group form
   */
  isCreatingGroup = signal(false);
  
  /**
   * New group name
   */
  newGroupName = '';
  newGroupDescription = '';

  /**
   * Handle add users button click
   */
  onAddUsers(group: Group): void {
    this.addUsers.emit(group);
  }

  /**
   * Show the create group form
   */
  onCreateGroupClick(): void {
    this.isCreatingGroup.set(true);
    this.newGroupName = '';
    this.newGroupDescription = '';
  }

  /**
   * Handle save button click for new group
   */
  onSaveGroup(): void {
    if (this.newGroupName.trim()) {
      this.createGroup.emit({ name: this.newGroupName.trim(), description: this.newGroupDescription });
      this.isCreatingGroup.set(false);
      this.newGroupName = '';
      this.newGroupDescription = '';
    }
  }

  /**
   * Handle cancel button click for new group
   */
  onCancelCreateGroup(): void {
    this.isCreatingGroup.set(false);
    this.cancelCreateGroup.emit();
  }

  /**
   * Handle delete group button click
   */
  onDeleteGroup(group: Group): void {
    this.deleteGroup.emit(group);
  }

  /**
   * Handle view members button click
   */
  onViewMembers(group: Group): void {
    this.viewMembers.emit(group);
  }

  /**
   * TrackBy function for ngFor directive to improve performance
   */
  trackById(index: number, item: Group): string {
    return item.id;
  }
}
